import React, { useState, useEffect, useCallback } from 'react';
import FacilityForm from './FacilityForm';
import MRTTableAdapter from './MRTTableAdapter';
import { createMRTColumnHelper } from 'material-react-table';
import { API_BASE } from '../utils/api';

interface Facility {
  id: number;
  name: string;
  address: string;
  responsible_person_id?: string;
  fda_registration_number?: string;
  annual_revenue?: number;
  employee_count?: number;
  facility_type: string;
  is_exempt?: boolean;
  exemption_reason?: string;
  next_renewal_date?: string;
  status?: string;
  registration_date?: string;
  renewal_date?: string;
  created_at?: string;
  updated_at?: string;
}

const FacilityDashboard: React.FC = () => {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingFacility, setEditingFacility] = useState<Facility | null>(null);
  const [viewingFacility, setViewingFacility] = useState<Facility | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Advanced filtering and sorting
  const [facilityTypeFilter, setFacilityTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [exemptFilter, setExemptFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const fetchFacilities = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const params = new URLSearchParams();
      if (facilityTypeFilter) params.append('facility_type', facilityTypeFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (exemptFilter) params.append('is_exempt', exemptFilter);
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);
      if (searchTerm) params.append('q', searchTerm);

      const response = await fetch(`${API_BASE}/facilities/?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const raw = await response.json();
      console.debug('[Facility] fetched list:', raw);
      const normalized = (raw || []).map((it: any) => ({
        id: it.id,
        name: it.name,
        address: it.address,
        facility_type: it.facility_type ?? it.facilityType ?? it.type ?? 'manufacturing',
        fda_registration_number: it.fda_registration_number ?? it.fdaRegistrationNumber ?? it.fda_reg_number ?? '',
        employee_count: it.employee_count ?? it.employeeCount ?? it.number_of_employees ?? it.num_employees ?? null,
        annual_revenue: it.annual_revenue ?? it.annualRevenue ?? it.revenue ?? null,
        is_exempt: it.is_exempt ?? it.isExempt ?? false,
        exemption_reason: it.exemption_reason ?? it.exemptionReason ?? it.exempt_reason ?? '',
        registration_date: it.registration_date ?? it.registrationDate ?? it.registered_at ?? null,
        renewal_date: it.renewal_date ?? it.next_renewal_date ?? it.renewalDate ?? it.nextRenewalDate ?? null,
        status: it.status ?? 'active',
        created_at: it.created_at ?? it.createdAt ?? it.created ?? null,
        updated_at: it.updated_at ?? it.updatedAt ?? it.updated ?? null,
        responsible_person_id: it.responsible_person_id ?? it.responsiblePersonId ?? it.responsible_person ?? null,
      }));
      setFacilities(normalized);
    } catch (error) {
      console.error('Error fetching facilities:', error);
      setError('Failed to fetch facilities. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [facilityTypeFilter, statusFilter, exemptFilter, sortBy, sortOrder, searchTerm]);

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  const handleSubmit = async (facilityData: Partial<Facility>) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Coerce numeric fields if passed as strings
      const payload: any = { ...facilityData };
      if (payload.annual_revenue !== undefined && payload.annual_revenue !== null) {
        payload.annual_revenue = typeof payload.annual_revenue === 'string'
          ? parseFloat(payload.annual_revenue.replace(/,/g, ''))
          : payload.annual_revenue;
      }
      if (payload.employee_count !== undefined && payload.employee_count !== null) {
        payload.employee_count = typeof payload.employee_count === 'string'
          ? parseInt(payload.employee_count.replace(/,/g, ''), 10)
          : payload.employee_count;
      }
      // responsible_person_id should remain as string (backend expects string)
      // No conversion needed - keep as string

      // Use backend snake_case keys to match API schema
      const aliasPayload: any = {
        name: payload.name,
        address: payload.address,
        facility_type: payload.facility_type,
        responsible_person_id: payload.responsible_person_id,
        fda_registration_number: payload.fda_registration_number,
        annual_revenue: payload.annual_revenue,
        employee_count: payload.employee_count,
        is_exempt: payload.is_exempt,
        exemption_reason: payload.exemption_reason,
        registration_date: payload.registration_date,
        renewal_date: payload.renewal_date,
        status: payload.status,
      };

      const url = editingFacility 
        ? `${API_BASE}/facilities/${editingFacility.id}`
        : `${API_BASE}/facilities/`;
      
      const method = editingFacility ? 'PUT' : 'POST';

      console.debug('[Facility] submitting payload:', aliasPayload);
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(aliasPayload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Backend error:', errorData);
        
        // Handle different error formats
        let errorMessage = 'Failed to save facility';
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (typeof errorData.detail === 'object' && errorData.detail.message) {
          errorMessage = errorData.detail.message;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((err: any) => err.msg || err.message || err).join(', ');
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        
        throw new Error(errorMessage);
      }

      const saved = await response.json().catch(() => null);
      if (saved) {
        console.debug('[Facility] saved response:', saved);
        const mapped: Facility = {
          id: saved.id,
          name: saved.name,
          address: saved.address,
          facility_type: saved.facility_type ?? saved.facilityType ?? 'manufacturing',
          fda_registration_number: saved.fda_registration_number ?? saved.fdaRegistrationNumber ?? '',
          employee_count: saved.employee_count ?? saved.employeeCount ?? null,
          annual_revenue: saved.annual_revenue ?? saved.annualRevenue ?? null,
          is_exempt: saved.is_exempt ?? saved.isExempt ?? false,
          exemption_reason: saved.exemption_reason ?? saved.exemptionReason ?? '',
          registration_date: saved.registration_date ?? saved.registrationDate ?? null,
          renewal_date: saved.renewal_date ?? saved.next_renewal_date ?? saved.renewalDate ?? null,
          status: saved.status ?? 'active',
          created_at: saved.created_at ?? saved.createdAt ?? null,
          updated_at: saved.updated_at ?? saved.updatedAt ?? null,
          responsible_person_id: saved.responsible_person_id ?? saved.responsiblePersonId ?? null,
        };
        setFacilities(prev => {
          if (editingFacility) {
            return prev.map(f => (f.id === editingFacility.id ? mapped : f));
          }
          return [mapped, ...prev];
        });
      } else {
      await fetchFacilities();
      }
      setShowForm(false);
      setEditingFacility(null);
      setSuccess(editingFacility ? 'Facility updated successfully!' : 'Facility registered successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving facility:', error);
      setError(error instanceof Error ? error.message : 'Failed to save facility');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this facility?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE}/facilities/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete facility');
      }

      await fetchFacilities();
      setSuccess('Facility deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error deleting facility:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete facility');
    }
  };

  const exportToExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const params = new URLSearchParams();
      if (facilityTypeFilter) params.append('facility_type', facilityTypeFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (exemptFilter) params.append('is_exempt', exemptFilter);
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);
      if (searchTerm) params.append('q', searchTerm);
      // Columns handled client-side by MRT

      const response = await fetch(`${API_BASE}/facilities/export/excel?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'facilities_export.xlsx';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setError('Failed to export to Excel. Please try again.');
    }
  };

  const exportToPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const params = new URLSearchParams();
      if (facilityTypeFilter) params.append('facility_type', facilityTypeFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (exemptFilter) params.append('is_exempt', exemptFilter);
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);
      if (searchTerm) params.append('q', searchTerm);
      // Columns handled client-side by MRT

      const response = await fetch(`${API_BASE}/facilities/export/pdf?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'facilities_export.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      setError('Failed to export to PDF. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getDaysUntil = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const columnHelper = createMRTColumnHelper<Facility>();
  const mrtColumns: any[] = [
    columnHelper.accessor('id', { header: 'ID', size: 80 }),
    columnHelper.accessor('name', { header: 'Name', size: 220 }),
    columnHelper.accessor((row) => row.facility_type ?? (row as any).facilityType ?? 'manufacturing', { id: 'facility_type', header: 'Type', size: 140, filterVariant: 'select' }),
    columnHelper.accessor('address', { header: 'Address', size: 260 }),
    columnHelper.accessor((row) => row.fda_registration_number ?? (row as any).fdaRegistrationNumber ?? '', { id: 'fda_registration_number', header: 'FDA Reg #', size: 140 }),
    columnHelper.display({
      id: 'employee_count',
      header: 'Employees',
      size: 110,
      Cell: ({ row }) => {
        const r: any = row.original || {};
        const val = r.employee_count ?? r.employeeCount ?? r.number_of_employees ?? r.num_employees;
        return <span>{val ?? ''}</span>;
      },
    }),
    columnHelper.accessor(row => (row.is_exempt ? 'Yes' : 'No'), { id: 'is_exempt', header: 'Exempt', size: 90, filterVariant: 'select' }),
    columnHelper.accessor(row => (row.status || 'active'), { id: 'status', header: 'Status', size: 110, filterVariant: 'select' }),
    columnHelper.accessor(row => {
      const d = (row as any).renewal_date ?? (row as any).next_renewal_date ?? (row as any).renewalDate;
      return d ? new Date(d).toISOString().slice(0,10) : '';
    }, { id: 'renewal_date', header: 'Next Renewal', size: 130, filterVariant: 'date-range' }),
    columnHelper.accessor(row => {
      const d = (row as any).created_at ?? (row as any).createdAt;
      return d ? new Date(d).toISOString().slice(0,10) : '';
    }, { id: 'created_at', header: 'Created', size: 120, filterVariant: 'date-range' }),
    columnHelper.accessor(row => {
      const d = (row as any).updated_at ?? (row as any).updatedAt;
      return d ? new Date(d).toISOString().slice(0,10) : '';
    }, { id: 'updated_at', header: 'Updated', size: 120, filterVariant: 'date-range' }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      size: 200,
      Cell: ({ row }) => (
        <div className="flex justify-end space-x-2">
          <button
            onClick={(e) => { e.stopPropagation(); openView(row.original); }}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            title="View Details"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            View
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setEditingFacility(row.original); setShowForm(true); }}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            title="Edit Facility"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(row.original.id); }}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
            title="Delete Facility"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete
          </button>
        </div>
      ),
    }),
  ];

  const openView = async (row: Facility) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/facilities/${row.id}`,{
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        console.debug('[Facility] view data:', data);
        const mapped: Facility = {
          id: data.id,
          name: data.name,
          address: data.address,
          facility_type: data.facility_type ?? data.facilityType ?? 'manufacturing',
          fda_registration_number: data.fda_registration_number ?? data.fdaRegistrationNumber ?? '',
          employee_count: data.employee_count ?? data.employeeCount ?? null,
          annual_revenue: data.annual_revenue ?? data.annualRevenue ?? null,
          is_exempt: data.is_exempt ?? data.isExempt ?? false,
          exemption_reason: data.exemption_reason ?? data.exemptionReason ?? '',
          registration_date: data.registration_date ?? data.registrationDate ?? null,
          renewal_date: data.renewal_date ?? data.next_renewal_date ?? data.renewalDate ?? null,
          status: data.status ?? 'active',
          created_at: data.created_at ?? data.createdAt ?? null,
          updated_at: data.updated_at ?? data.updatedAt ?? null,
          responsible_person_id: data.responsible_person_id ?? data.responsiblePersonId ?? null,
        };
        setViewingFacility(mapped);
      } else {
        setViewingFacility(row);
      }
    } catch {
      setViewingFacility(row);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-indigo-600 mx-auto"></div>
          <h2 className="text-xl font-semibold text-gray-800 mt-6">Loading Facilities...</h2>
          <p className="text-gray-500 mt-2">Please wait while we fetch your facility data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="bg-indigo-100 p-3 rounded-lg">
                <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
        <div>
                <h1 className="text-3xl font-bold text-gray-900">Facility Management</h1>
                <p className="text-gray-600 mt-1">Register and manage your cosmetic manufacturing facilities</p>
              </div>
        </div>
        <div className="flex items-center space-x-3">
              <div className="bg-gray-100 px-4 py-2 rounded-lg">
                <span className="text-sm font-medium text-gray-700">Total Facilities: </span>
                <span className="text-lg font-bold text-indigo-600">{facilities.length}</span>
              </div>
          <button
            onClick={() => setShowForm(true)}
                className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-6 py-3 rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200 flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Facility
          </button>
        </div>
      </div>
      </div>

        {/* Success Alert */}
        {success && (
          <div className="bg-green-50 border-l-4 border-green-400 rounded-lg p-4 mb-6 animate-in slide-in-from-top-2 duration-300">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-green-800">Success</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>{success}</p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => setSuccess(null)}
                    className="text-sm font-medium text-green-800 hover:text-green-900 underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Error Alert */}
      {error && (
          <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-4 mb-6 animate-in slide-in-from-top-2 duration-300">
          <div className="flex items-start">
            <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
              <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => setError(null)}
                    className="text-sm font-medium text-red-800 hover:text-red-900 underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters removed per request. Basic UI only shows Add and table. */}

        {/* Facility Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-300">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-indigo-100 p-2 rounded-lg">
                      <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
          </div>
                    <h2 className="text-2xl font-bold text-gray-900">
            {editingFacility ? 'Edit Facility' : 'Register New Facility'}
          </h2>
                  </div>
          <button
            onClick={() => {
              setShowForm(false);
              setEditingFacility(null);
            }}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
          >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
          </button>
                </div>
              </div>
              <div className="p-6">
                <FacilityForm
                  onSubmit={handleSubmit}
                  initialData={editingFacility || undefined}
                />
              </div>
            </div>
        </div>
      )}

        {/* Facilities Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Facilities Overview</h3>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span>{facilities.length} facilities registered</span>
              </div>
            </div>
          </div>
          <div className="p-0">
      <MRTTableAdapter<Facility> columns={mrtColumns} data={facilities} />
          </div>
        </div>

      {/* Facility Details Modal */}
      {viewingFacility && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-300">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Facility Details</h3>
                  </div>
                <button
                  onClick={() => setViewingFacility(null)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              </div>
              <div className="p-6">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Facility Name</label>
                      <p className="text-lg font-medium text-gray-900">{viewingFacility?.name}</p>
                  </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Facility Type</label>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 capitalize">
                        {viewingFacility?.facility_type}
                      </span>
                  </div>
                    <div className="bg-gray-50 p-4 rounded-lg md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
                      <p className="text-gray-900">{viewingFacility?.address}</p>
                  </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">FDA Registration Number</label>
                      <p className="text-gray-900 font-mono">{viewingFacility?.fda_registration_number || 'N/A'}</p>
                  </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Responsible Person ID</label>
                      <p className="text-gray-900 font-mono">{viewingFacility?.responsible_person_id || 'N/A'}</p>
                  </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Annual Revenue</label>
                      <p className="text-lg font-semibold text-green-600">
                      {viewingFacility?.annual_revenue !== undefined && viewingFacility?.annual_revenue !== null
                        ? `$${Number(viewingFacility.annual_revenue).toLocaleString()}`
                        : 'N/A'}
                    </p>
                  </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Employee Count</label>
                      <p className="text-lg font-semibold text-blue-600">{viewingFacility?.employee_count || 'N/A'}</p>
                  </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        viewingFacility?.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {viewingFacility?.status || 'Active'}
                      </span>
                  </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Exemption Status</label>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        viewingFacility?.is_exempt ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                      {viewingFacility?.is_exempt ? 'Exempt' : 'Not Exempt'}
                      </span>
                  </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Registration Date</label>
                      <p className="text-gray-900">
                      {viewingFacility?.registration_date ? formatDate(viewingFacility.registration_date) : 'N/A'}
                    </p>
                  </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Renewal Date</label>
                      <p className="text-gray-900">
                      {viewingFacility?.renewal_date ? formatDate(viewingFacility.renewal_date) : 'N/A'}
                    </p>
                  </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Created At</label>
                      <p className="text-gray-900">
                      {viewingFacility?.created_at ? formatDate(viewingFacility.created_at) : 'N/A'}
                    </p>
                  </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Last Updated</label>
                      <p className="text-gray-900">
                      {viewingFacility?.updated_at ? formatDate(viewingFacility.updated_at) : 'N/A'}
                    </p>
                    </div>
                  </div>
                  {viewingFacility?.exemption_reason && (
                    <div className="bg-yellow-50 p-4 rounded-lg md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Exemption Reason</label>
                      <p className="text-gray-900">{viewingFacility.exemption_reason}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
    </div>
  );
};

export default FacilityDashboard;